// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------
export class AuthenticationError extends Error {
    constructor(message) {
        super(message);
        this.name = "AuthenticationError";
    }
}
export class SessionError extends Error {
    status;
    correlationId;
    constructor(message, status, correlationId) {
        super(message);
        this.status = status;
        this.correlationId = correlationId;
        this.name = "SessionError";
    }
}
export class WorkspaceUnavailableError extends SessionError {
    retryAfter;
    constructor(message, status, retryAfter, correlationId) {
        super(message, status, correlationId);
        this.retryAfter = retryAfter;
        this.name = "WorkspaceUnavailableError";
    }
}
export class ReadContextError extends SessionError {
    constructor(message, status, correlationId) {
        super(message, status, correlationId);
        this.name = "ReadContextError";
    }
}
export class WriteContextError extends SessionError {
    constructor(message, status, correlationId) {
        super(message, status, correlationId);
        this.name = "WriteContextError";
    }
}
export function isAuthenticationError(error) {
    return error instanceof AuthenticationError;
}
function createCorrelationId() {
    return globalThis.crypto?.randomUUID?.() ?? `corr_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}
function isStringArray(value) {
    return Array.isArray(value) && value.every((item) => typeof item === "string");
}
function parseBootstrapResponse(raw) {
    const payload = raw;
    const user = payload.user;
    const context = payload.context;
    const entitlements = payload.entitlements;
    if (!user ||
        typeof user.user_id !== "string" ||
        typeof user.tenant_id !== "string" ||
        typeof user.email !== "string") {
        throw new SessionError("Session bootstrap payload is missing required user fields", 500);
    }
    if (typeof payload.audience_token !== "string" ||
        typeof payload.context_claims_token !== "string" ||
        typeof payload.expires_at !== "string") {
        throw new SessionError("Session bootstrap payload is missing token fields", 500);
    }
    if (!entitlements ||
        !isStringArray(entitlements.enabled_apps) ||
        !isStringArray(entitlements.coarse_roles)) {
        throw new SessionError("Session bootstrap payload has invalid entitlements", 500);
    }
    if (context != null &&
        (typeof context.tenant_id !== "string" ||
            (context.chain_id != null && typeof context.chain_id !== "string") ||
            (context.store_id != null && typeof context.store_id !== "string"))) {
        throw new SessionError("Session bootstrap payload has invalid context", 500);
    }
    return {
        user: {
            userId: user.user_id,
            tenantId: user.tenant_id,
            email: user.email,
        },
        accessToken: payload.audience_token,
        context: context != null
            ? {
                tenantId: context.tenant_id,
                chainId: context.chain_id ?? undefined,
                storeId: context.store_id ?? undefined,
            }
            : null,
        entitlements: {
            enabledApps: entitlements.enabled_apps,
            coarseRoles: entitlements.coarse_roles,
        },
        expiresAt: payload.expires_at,
    };
}
function toExecutionContext(payload) {
    return {
        tenantId: payload.tenant_id,
        chainId: payload.chain_id ?? undefined,
        storeId: payload.store_id ?? undefined,
    };
}
async function bootstrapHttp(workspaceBaseUrl, audience, fetchImpl) {
    const baseUrl = workspaceBaseUrl.replace(/\/+$/, "");
    const url = `${baseUrl}/api/v1/session?audience=${encodeURIComponent(audience)}`;
    const correlationId = createCorrelationId();
    const response = await fetchImpl(url, {
        method: "GET",
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
            "X-Correlation-Id": correlationId,
        },
    });
    const responseCorrelationId = response.headers?.get?.("X-Correlation-Id") ?? correlationId;
    if (response.status === 401) {
        throw new AuthenticationError("Authentication required");
    }
    if (response.status === 503) {
        throw new WorkspaceUnavailableError(`Workspace unavailable: ${response.status} ${response.statusText}`, response.status, response.headers?.get?.("Retry-After"), responseCorrelationId);
    }
    if (!response.ok) {
        throw new SessionError(`Session bootstrap failed: ${response.status} ${response.statusText}`, response.status, responseCorrelationId);
    }
    const data = await response.json();
    return parseBootstrapResponse(data);
}
// ---------------------------------------------------------------------------
// Internal: token lifecycle
// ---------------------------------------------------------------------------
function shouldRefreshSoon(expiresAt) {
    if (!expiresAt)
        return false;
    const expiresAtMs = Date.parse(expiresAt);
    if (Number.isNaN(expiresAtMs))
        return true;
    return expiresAtMs - Date.now() <= 120_000;
}
// ---------------------------------------------------------------------------
// Public factory
// ---------------------------------------------------------------------------
export async function createWorkspaceSession(config) {
    const { workspaceBaseUrl, audience } = config;
    const fetchImpl = config.fetchImpl ?? fetch;
    const baseUrl = workspaceBaseUrl.replace(/\/+$/, "");
    const defaultOnAuthError = (error) => {
        void error;
        const returnTo = encodeURIComponent(typeof window !== "undefined" ? window.location.href : "");
        const loginUrl = `${baseUrl}/login?return_to=${returnTo}`;
        if (typeof window !== "undefined") {
            window.location.href = loginUrl;
        }
    };
    const onAuthError = config.onAuthenticationError ?? defaultOnAuthError;
    // --- Bootstrap (eager) ---------------------------------------------------
    const initial = await bootstrapHttp(workspaceBaseUrl, audience, fetchImpl);
    // --- Token store (internal) ----------------------------------------------
    let accessToken = initial.accessToken;
    let expiresAt = initial.expiresAt;
    function setTokens(result) {
        accessToken = result.accessToken;
        expiresAt = result.expiresAt;
    }
    function clearTokens() {
        accessToken = "";
        expiresAt = null;
    }
    // --- Refresh (single-flight) ---------------------------------------------
    let refreshInFlight = null;
    async function refreshSingleFlight() {
        refreshInFlight ??= bootstrapHttp(workspaceBaseUrl, audience, fetchImpl).finally(() => {
            refreshInFlight = null;
        });
        return refreshInFlight;
    }
    // --- Authenticated fetch -------------------------------------------------
    async function authenticatedFetch(input, init) {
        if (accessToken && shouldRefreshSoon(expiresAt)) {
            try {
                setTokens(await refreshSingleFlight());
            }
            catch (error) {
                if (error instanceof AuthenticationError) {
                    clearTokens();
                    onAuthError(error);
                    throw error;
                }
            }
        }
        const headers = new Headers(init?.headers);
        if (accessToken) {
            headers.set("Authorization", `Bearer ${accessToken}`);
        }
        const requestInit = {
            ...init,
            headers,
        };
        let response = await fetchImpl(input, requestInit);
        if (response.status === 401) {
            try {
                const result = await refreshSingleFlight();
                setTokens(result);
                headers.set("Authorization", `Bearer ${result.accessToken}`);
                const retryInit = {
                    ...init,
                    headers,
                };
                response = await fetchImpl(input, retryInit);
            }
            catch (error) {
                if (error instanceof AuthenticationError) {
                    clearTokens();
                    onAuthError(error);
                }
                else if (!(error instanceof WorkspaceUnavailableError)) {
                    clearTokens();
                }
                throw error;
            }
        }
        return response;
    }
    // --- Context operations --------------------------------------------------
    async function readContext() {
        const url = `${baseUrl}/api/v1/me/context`;
        const correlationId = createCorrelationId();
        const response = await authenticatedFetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "X-Correlation-Id": correlationId,
            },
        });
        if (!response.ok) {
            throw new ReadContextError(`Failed to read workspace context: ${response.status} ${response.statusText}`, response.status, response.headers.get("X-Correlation-Id") ?? correlationId);
        }
        const payload = (await response.json());
        const etag = response.headers.get("ETag");
        if (payload === null) {
            return { context: null, etag };
        }
        if (typeof payload.tenant_id !== "string" ||
            typeof payload.context_version !== "number" ||
            (payload.chain_id !== null && typeof payload.chain_id !== "string") ||
            (payload.store_id !== null && typeof payload.store_id !== "string")) {
            throw new ReadContextError("Workspace context payload is malformed", 500);
        }
        return { context: toExecutionContext(payload), etag };
    }
    async function writeContext(context, etag) {
        const url = `${baseUrl}/api/v1/me/context`;
        const correlationId = createCorrelationId();
        const body = {
            context_version: 1,
            tenant_id: context.tenantId,
            chain_id: context.chainId ?? null,
            store_id: context.storeId ?? null,
        };
        const response = await authenticatedFetch(url, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "If-Match": etag,
                "X-Correlation-Id": correlationId,
            },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            throw new WriteContextError(`Failed to write workspace context: ${response.status} ${response.statusText}`, response.status, response.headers.get("X-Correlation-Id"));
        }
        const responsePayload = (await response.json());
        return {
            context: toExecutionContext(responsePayload),
            etag: response.headers.get("ETag"),
        };
    }
    function syncContext(context, etag) {
        writeContext(context, etag).catch((err) => {
            console.error("Failed to sync context to workspace (ignored):", err);
        });
    }
    // --- Redirect helper (public, for manual trigger) ------------------------
    function redirectToLogin(options) {
        const loginPath = options?.loginPath ?? "/login";
        const normalizedPath = `/${loginPath.replace(/^\/+/, "")}`;
        const returnTo = encodeURIComponent(typeof window !== "undefined" ? window.location.href : "");
        const loginUrl = `${baseUrl}${normalizedPath}?return_to=${returnTo}`;
        if (typeof window !== "undefined") {
            window.location.href = loginUrl;
        }
    }
    return {
        fetch: authenticatedFetch,
        user: initial.user,
        context: initial.context,
        entitlements: initial.entitlements,
        getAccessToken: () => accessToken,
        redirectToLogin,
        readContext,
        writeContext,
        syncContext,
    };
}
//# sourceMappingURL=session.js.map